import {
  useAddCheckoutPromoCode,
  useRemoveCheckoutPromoCode,
} from 'src/react_query/queries/hooks'
import { CheckoutFragment } from 'src/saleor_graphql/generated/graphql'
import usePersistedUserData from './usePersistedUserData'
import { parseCheckout } from 'src/react_query/utils'

const useUpdateCheckoutVouchers = () => {
  const { account } = usePersistedUserData()
  const {
    mutate: addCheckoutPromoCode,
    isLoading: isAddLoading,
    isError: isAddError,
  } = useAddCheckoutPromoCode()
  const {
    mutate: removeCheckoutPromoCode,
    isLoading: isRemoveLoading,
    isError: isRemoveError,
  } = useRemoveCheckoutPromoCode()

  const updateCheckoutVouchers = (checkout: CheckoutFragment) => {
    // New voucher rules
    // 1. Add voucherCode meta field to saleor variant
    // 2. If voucher is found we add it automatically
    // This is useful in scenarios where we want to give a discount to users who combined certain products together
    // E.G If you add a subscription and a card pack at the same time we give the user the discount subscribed members get
    // a discount on their card pack. This works by creating a voucher that only applies to card pack products. If no card pack is added the voucher
    // will do nothing.

    // TODO: I'm not sure if the full access affiliate stuff is still used, keeping it here for now
    const affiliateFreeVoucherCode = 'affiliatediscount'
    const parsedCheckout = parseCheckout(checkout)

    const hasFullAccessFreeAffiliate =
      account?.hasFullAccessFreeAffiliate ||
      parsedCheckout.lines.some(line => {
        return line.variant.gives90DayFreeAffiliate?.toLowerCase() === 'true'
      })

    const lineVoucherCodes = Array.from(
      new Set(
        parsedCheckout.lines
          .map(line => {
            return line.variant.voucherCode ?? '' // These were .toLowerCase but vouchers are case sensitive
          })
          .filter(voucher => voucher !== '')
          .concat(hasFullAccessFreeAffiliate ? [affiliateFreeVoucherCode] : []),
      ),
    )

    const suggestedAddons = parsedCheckout.lines
      .map(line => ({
        promo: line.variant.parsedAttributes.suggestedAddonPromo,
        addon: line.variant.parsedAttributes.suggestedAddon,
      }))
      .filter(a => Boolean(a.addon))

    const checkoutLineVariants = parsedCheckout.lines.map(
      line => line.variant.id,
    )
    const accountType = account ? account.type : 'NON_AUTH'
    const suggestedAddonPromosToApply = suggestedAddons
      .filter(a => {
        const { addon, promo } = a

        const isSuggestedProductInCart = addon?.reference
          ? checkoutLineVariants.includes(addon?.reference)
          : false
        const isValidUserType = promo.forUserTypes.includes(accountType)

        return isSuggestedProductInCart && isValidUserType
      })
      .map(addon => addon.promo.code)
      .filter((c): c is string => Boolean(c))

    const shouldHaveVoucher =
      suggestedAddonPromosToApply.length > 0 || lineVoucherCodes.length > 0

    if (!parsedCheckout.voucherCode && shouldHaveVoucher) {
      addCheckoutPromoCode({
        id: parsedCheckout.id,
        promoCode: lineVoucherCodes[0] || suggestedAddonPromosToApply[0],
      })
    }
    if (parsedCheckout.voucherCode && !shouldHaveVoucher) {
      removeCheckoutPromoCode({
        id: parsedCheckout.id,
        promoCode: parsedCheckout.voucherCode,
      })
    }
  }

  return {
    updateCheckoutVouchers,
    isLoading: isAddLoading || isRemoveLoading,
    isError: isAddError || isRemoveError,
  }
}

export default useUpdateCheckoutVouchers
